import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Toolbar, Box, Breadcrumbs } from "@mui/material";
import { useAppState } from "Context/StateContext";
import { useViewport } from "Hooks/useViewPort";
import UserService from "Services/user.service";
import { useQuery } from "@tanstack/react-query";
import { useDumontPanel } from "Context/DumontPanelContext";
import { PRIVATE_ROUTES, PUBLIC_ROUTES, USER_PERMISSIONS, USER_TOKEN } from "Constants";
import { Routes } from "Util/enums";
import jwtDecode from "jwt-decode";
import moment from "moment";
import { useResourcePermissionsActions } from "./useResourcePermissionsActions";
import { COLLAPSED_WIDTH, EXPANDED_WIDTH } from "Components/SideBar/constants";
import { SIDEBAR_TOTAL_WIDTH } from "Components/DumontSidebar/constants";
import NotificationBlock from "../../../layout/Sidebar/NotificationBlock";
import LanguageBlock from "../../../layout/Sidebar/LanguageBlock";
import { Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import { useTranslation } from "react-i18next";
import SvgIconUrl from "../../../helpers/SvgIcon/SvgIconUrl";

import { menuConfigMain } from "Components/SideBar/config/menuConfigMain";
import { menuConfigSettings } from "Components/SideBar/config/menuConfigSettings";
import type { MenuItem } from "Components/SideBar/types";

import {
  AppBarStyled,
  Root,
  Container,
  ContainerPageTitle,
  ContainerActions,
  PageTitleText,
  ContainerActionDumont,
  BreadcrumbItem,
} from "./styles";
import { normalizeBreadcrumbPath, getBreadcrumb, getManualCrumbs } from "./helpers";
import { getBrandCustom } from "Components/AppGrid/shipeezi-new-grid/utils/getCustomColor";

const BREAKPOINT = 990;
const DUMONTAI = require("Components/Generic/Icons/IconDumont/dumont.svg");
const DUMONTAIICON = require("Assets/icons/voice-plus.svg");
const DASHICON = require("Assets/icons/dashboard.svg");

interface HeaderProps {
  sidebarPinned: boolean;
}

type Indexed = { node: MenuItem; parentKey: string | null };

function makeIndex(trees: MenuItem[]): Map<string, Indexed[]> {
  const map = new Map<string, Indexed[]>();

  const add = (key: string, entry: Indexed) => {
    const list = map.get(key) ?? [];
    list.push(entry);
    map.set(key, list);
  };

  const walk = (nodes: MenuItem[], parentKey: string | null) => {
    for (const n of nodes) {
      const k = n.key.toLowerCase();
      add(k, { node: n, parentKey });
      if (n.children?.length) walk(n.children, k);
    }
  };

  walk(trees, null);
  return map;
}

function buildPath(index: Map<string, Indexed[]>, deepestKey: string): MenuItem[] {
  const candidates = index.get(deepestKey) ?? [];
  if (candidates.length === 0) return [];

  let current = candidates[0];
  const path: MenuItem[] = [current.node];

  let childKey: string | null = deepestKey;
  let parentKey: string | null = current.parentKey;

  while (parentKey) {
    const parents = index.get(parentKey) ?? [];
    const realParent =
      parents.find((p) => (p.node.children ?? []).some((c) => c.key.toLowerCase() === childKey)) ??
      parents[0];

    if (!realParent) break;

    path.unshift(realParent.node);
    childKey = parentKey;
    parentKey = realParent.parentKey;
  }

  return path;
}

export const Header: React.FC<HeaderProps> = ({ sidebarPinned }) => {
  const { width } = useViewport();
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const {
    currentUser,
    setUserPermissions,
    userPermissions,
    userUid,
    isDumontSidebarOpen,
    setIsDumontSidebarOpen,
    userLogout,
  } = useAppState();
  const { setGroupSelected, setNavBarLinkSelected } = useResourcePermissionsActions();
  const { isPanelOpen, openPanel } = useDumontPanel();
  const leftMargin = sidebarPinned ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
  const rightMargin = isPanelOpen ? SIDEBAR_TOTAL_WIDTH : 0;

  const showActions = width > BREAKPOINT;

  const isHeaderHidden = useMemo(() => {
    const p = location.pathname.toLowerCase();
    return (
      p.includes("signin") ||
      p.includes("onboarding") ||
      p.includes(Routes.ESTIMATE_QUOTE.toLowerCase())
    );
  }, [location.pathname]);

  if (isHeaderHidden) return null;

  useQuery(["permissions.get-by-current-user"], () => UserService.getUserPermissions(), {
    enabled:
      !location.pathname?.includes("update-password") &&
      (!!localStorage.getItem(USER_TOKEN) ||
        userPermissions?.length === 0 ||
        !localStorage.getItem(USER_PERMISSIONS)),
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (!response?.data) return;
      localStorage.setItem(USER_PERMISSIONS, JSON.stringify(response.data));
      setUserPermissions(response.data);
    },
  });

  useEffect(() => {
    const doLogout = () => {
      userLogout();
      history.replace(PUBLIC_ROUTES.SIGN_IN);
    };

    if (!userUid) return doLogout();

    const decoded: { timeout?: string } = jwtDecode(userUid);
    if (!decoded?.timeout) return doLogout();

    if (moment().utc().isAfter(moment(decoded.timeout).utc())) doLogout();
  }, [userUid, history, userLogout]);

  const onAskDumont = () => {
    openPanel();
  };

  const goNotifications = () => {
    setGroupSelected(undefined);
    setNavBarLinkSelected(undefined);
    history.push(PRIVATE_ROUTES.NOTIFICATIONS);
  };

  const trees = useMemo(() => [...menuConfigMain, ...menuConfigSettings], []);
  const effectivePath = useMemo(
    () => normalizeBreadcrumbPath(location.pathname),
    [location.pathname]
  );

  const manualCrumbs = useMemo(() => getManualCrumbs(effectivePath, trees), [effectivePath, trees]);

  const crumbs = useMemo(
    () =>
      manualCrumbs ??
      getBreadcrumb(
        { pathname: effectivePath, search: location.search, state: (location as any).state },
        trees,
        currentUser,
        t
      ),
    [manualCrumbs, effectivePath, location.search, (location as any).state, trees, currentUser]
  );

  const isDumont = useMemo(
    () => effectivePath.toLowerCase().startsWith(PRIVATE_ROUTES.DUMONT_AI.toLowerCase()),
    [effectivePath]
  );

  const isAskDumontActive = isDumont || isPanelOpen || isDumontSidebarOpen;

  const crumbIcon = (crumbs[0]?.icon as string | undefined) ?? null;

  return (
    <Root>
      <AppBarStyled elevation={0}>
        <Toolbar>
          <Container leftMargin={leftMargin} rightMargin={rightMargin}>
            <ContainerPageTitle>
              {crumbIcon !== null && (
                <SvgIconUrl
                  src={crumbIcon}
                  size={20}
                  color={getBrandCustom("primaryColor", "#002445")}
                />
              )}
              {isDumont ? (
                // <PageTitleText>{t("askDumont")}</PageTitleText>
                <></>
              ) : (
                <Breadcrumbs separator="/">
                  {crumbs.map((crumb, index) => (
                    <BreadcrumbItem
                      key={`${crumb.key}-${index}`}
                      hasPointer={
                        (!!crumb.path && crumbs.length > 1 && index !== 0) ||
                        (crumbs.length > 1 && crumb.path?.includes("network"))
                      }
                      onClick={() => {
                        if (crumb.path && crumbs.length > 1) {
                          history.push({
                            pathname: crumb.path,
                            state: crumb.path.includes("analytics")
                              ? {
                                  reportsFilter: crumb.state?.reportsFilter || "",
                                }
                              : {
                                  ...crumb.state,
                                },
                          });
                        }
                      }}
                    >
                      <PageTitleText noMargin={index !== 0}>{t(crumb.label)}</PageTitleText>
                    </BreadcrumbItem>
                  ))}
                </Breadcrumbs>
              )}
            </ContainerPageTitle>

            {showActions && (
              <ContainerActions>
                {!isAskDumontActive && (
                  <ContainerActionDumont>
                    <Button
                      onClick={onAskDumont}
                      customColor={Color.AI_DUMONT}
                      startIcon={
                        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                          <SvgIconUrl src={DUMONTAI} size={14} />
                        </Box>
                      }
                      sx={{
                        borderRadius: 999,
                        fontFamily: "Inter, Manrope, sans-serif",
                        fontSize: 16,
                        fontWeight: 500,
                        height: 32,
                      }}
                    >
                      {t("askDumont")}
                    </Button>
                  </ContainerActionDumont>
                )}

                <LanguageBlock />

                <NotificationBlock
                  handleGoToNotificationsScreen={goNotifications}
                  handleSelected={() => {
                    setGroupSelected(undefined);
                    setNavBarLinkSelected(undefined);
                  }}
                />
              </ContainerActions>
            )}
          </Container>
        </Toolbar>
      </AppBarStyled>
    </Root>
  );
};
